import { ArrowRight } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { usePageMeta } from '@/lib/seo';
import { BlogBody } from '@/modules/marketing/blog/BlogBody';
import { BlogCard } from '@/modules/marketing/blog/BlogCard';
import { formatDate, postBySlug, relatedPosts } from '@/modules/marketing/blog/posts';
import { SiteFooter } from '@/modules/marketing/SiteFooter';
import { SiteHeader } from '@/modules/marketing/SiteHeader';

/**
 * One post.
 *
 * ## What a reader is given, in order
 *
 * The answer's context first: the topic, the heading, one sentence, who wrote it and when. Then the
 * picture, then the post. A list of the sections goes above the body only when there are enough of
 * them to be worth jumping between; a post with three headings is read, not navigated.
 *
 * ## The way out is one thing, not four
 *
 * Under the post there is one box that says what the platform does about the problem the post was
 * about, and three more posts. No newsletter form, no share bar and no comments: nothing that asks
 * somebody who has just been helped for something before they leave.
 *
 * An unknown slug goes to the index rather than rendering an empty page, and is not indexed on the
 * way past.
 */

const ORIGIN = 'https://techiegain.com';

export function BlogPostPage() {
  const { slug } = useParams();
  const post = postBySlug(slug);

  // Before the early return, so the hook is never called conditionally.
  usePageMeta({
    title: post?.seoTitle ?? 'Post not found',
    description: post?.description ?? 'That post could not be found.',
    noindex: post === undefined,
    image:
      post === undefined
        ? undefined
        : { url: `${ORIGIN}${post.image.ogSrc}`, width: 1200, height: 630, alt: post.image.alt },
    ogType: 'article',
  });

  if (post === undefined) {
    return <Navigate to="/blog" replace />;
  }

  const sections = post.blocks.flatMap((block) =>
    block.type === 'heading' && block.level === 2 ? [block] : [],
  );
  const more = relatedPosts(post);

  return (
    <div className="bg-background min-h-screen">
      <SiteHeader />

      <article>
        <header className="relative overflow-hidden">
          <div
            aria-hidden
            className="from-primary/10 pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-gradient-to-b to-transparent"
          />
          <div className="relative mx-auto max-w-3xl px-6 pt-14 pb-9 sm:pt-20">
            <nav aria-label="Breadcrumb" className="text-muted-foreground text-sm">
              <ol className="flex flex-wrap items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-foreground transition-colors">
                    Home
                  </Link>
                </li>
                <li aria-hidden>/</li>
                <li>
                  <Link to="/blog" className="hover:text-foreground transition-colors">
                    Blog
                  </Link>
                </li>
              </ol>
            </nav>

            <p className="text-primary mt-9 text-xs font-medium tracking-[0.18em] uppercase">{post.topic}</p>
            <h1 className="mt-4 text-3xl leading-tight font-semibold tracking-tight text-balance sm:text-5xl">
              {post.title}
            </h1>
            <p className="text-muted-foreground mt-5 text-lg leading-relaxed">{post.description}</p>
            <p className="text-muted-foreground/90 mt-6 text-sm">
              {post.author.url === null ? (
                <span>By {post.author.name}</span>
              ) : (
                <span>
                  By{' '}
                  <a href={post.author.url} className="text-foreground underline underline-offset-4">
                    {post.author.name}
                  </a>
                </span>
              )}
              <span aria-hidden> · </span>
              <time dateTime={post.published}>{formatDate(post.published)}</time>
              <span aria-hidden> · </span>
              {post.minutes} min read
            </p>
          </div>
        </header>

        <div className="mx-auto max-w-4xl px-6">
          <img
            src={post.image.src}
            alt={post.image.alt}
            width={post.image.width}
            height={post.image.height}
            decoding="async"
            className="border-border/60 aspect-video w-full rounded-2xl border object-cover"
          />
        </div>

        <div className="mx-auto max-w-3xl px-6 pt-10 pb-24">
          {sections.length >= 4 && (
            <nav aria-label="In this post" className="border-border/60 bg-muted/10 rounded-xl border px-5 py-4">
              <p className="text-foreground text-sm font-medium">In this post</p>
              <ol className="text-muted-foreground mt-3 list-decimal space-y-1.5 pl-5 text-[15px]">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="hover:text-foreground transition-colors">
                      {section.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <BlogBody blocks={post.blocks} />

          <aside className="border-border/60 bg-muted/10 mt-16 rounded-2xl border p-7">
            <h2 className="text-xl font-semibold tracking-tight text-balance">{post.cta.heading}</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">{post.cta.text}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={post.cta.to}
                className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
              >
                {post.cta.label}
                <ArrowRight className="size-4" aria-hidden />
              </Link>
              <Link
                to="/register"
                className="border-border hover:bg-muted/30 inline-flex items-center rounded-md border px-5 py-2.5 text-sm font-medium transition-colors"
              >
                Start free trial
              </Link>
            </div>
          </aside>
        </div>

        {more.length > 0 && (
          <section aria-labelledby="keep-reading" className="mx-auto max-w-6xl px-6 pb-24">
            <h2 id="keep-reading" className="text-xl font-semibold tracking-tight">
              Keep reading
            </h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {more.map((related) => (
                <BlogCard key={related.slug} post={related} level={3} />
              ))}
            </div>
          </section>
        )}
      </article>

      <SiteFooter />
    </div>
  );
}
